import { Button, Checkbox, Divider, FormControlLabel, Grid, TextField, Typography, useTheme } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { createCertificates, updateOlympicsSettings } from '../../api/content-api';
import SnackbarContext from '../../store/snackbar-context';
import Loader from '../../ui/Loader';
import AuthContext from '../../store/auth-context';
import { useNavigate } from 'react-router-dom';
import { loginPage, panelPage } from '../../store/fixed-routing';
import Olympics from '../../model/Olympics';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/pl';
import SaveIcon from '@mui/icons-material/Save';
import ReplayIcon from '@mui/icons-material/Replay';
import ConfirmationDialog from './ConfirmationDialog';
import OlympicsContext from '../../store/olympics-context';
import NewOlympicsDialog from './NewOlympicsDialog';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useCustomTheme } from '../../store/theme-context';

type SettingsRowProps = {
	firstChild: any;
	secondChild?: any;
	thirdChild?: any;
};
const SettingsRow = ({ firstChild, secondChild, thirdChild }: SettingsRowProps) => {
	return (
		<Grid
			item
			container
			gap={'20px'}
			flexWrap={'nowrap'}
			mt={2}
		>
			<Grid
				item
				container
			>
				{firstChild}
			</Grid>
			{secondChild && (
				<Grid
					item
					container
				>
					{secondChild}
				</Grid>
			)}
			{thirdChild && (
				<Grid
					item
					container
				>
					{thirdChild}
				</Grid>
			)}
		</Grid>
	);
};
const SettingsPage = () => {
	const navigate = useNavigate();
	const { palette } = useTheme();
	const { isHighContrast } = useCustomTheme();
	const { setMsg } = useContext(SnackbarContext);
	const { logout, token, isAdmin } = useContext(AuthContext);
	const { olympics, setOlympics } = useContext(OlympicsContext);
	const [openNewOlympicsDialog, setOpenNewOlympicsDialog] = useState(false);
	const [dialogContent, setDialogContent] = useState<string | null>(null);
	const [sendingRequest, setSendingRequest] = useState(false);
	const [okFunction, setOkFunction] = useState<Function | null>(null);

	useEffect(() => {
		if (!isAdmin) {
			navigate(panelPage);
		}
	}, []);

	const setProperty = (obj: any, path: string, value: any): any => {
		const [head, ...rest] = path.split('.');

		return {
			...obj,
			[head]: rest.length ? setProperty(obj[head], rest.join('.'), value) : value,
		};
	};
	const handlePointsChange = (event: any, path: string) => {
		const value = Number(event.target.value);
		if (olympics) {
			setOlympics(setProperty(olympics, path, value));
		}
	};

	const handleValueChange = (path: string, value: any) => {
		if (olympics) {
			setOlympics(setProperty(olympics, path, value));
		}
	};

	const handleSettings = (path: string, value: any) => {
		if (olympics) {
			setOlympics(setProperty(olympics, path, value.target.value));
		}
	};

	const closeDialog = () => {
		setSendingRequest(false);
		setOkFunction(null);
		setDialogContent(null);
	};

	const updateOlympics = () => {
		if (olympics) {
			setSendingRequest(true);
			updateOlympicsSettings(token, olympics)
				.then((response) => {
					if (response.status === 200) {
						setOlympics(Olympics.fromApiResponse(response.data));
						setMsg({ msg: 'Ustawienia zostały zaktualizowane', severity: 'success' });
					} else {
						setMsg({ msg: response.data });
					}
					closeDialog();
				})
				.catch((exception) => {
					console.error(exception);
					if (exception.response.status === 401 || exception.response.status === 403) {
						logout();
						navigate(loginPage);
						setMsg({ msg: 'Nie masz uprawnień albo Twoja sesja wygasła' });
					} else {
						setMsg({ msg: 'Błąd serwera. Spróbuj za chwilę' });
					}
					closeDialog();
				});
		} else {
			closeDialog();
		}
	};

	const generateCertificates = (type: string) => {
		if (type) {
			setSendingRequest(true);
			createCertificates(token, { type })
				.then((response) => {
					if (response.status === 200) {
						setMsg({ msg: response.data.message, severity: 'success' });
					} else {
						setMsg({ msg: response.data });
					}
					closeDialog();
				})
				.catch((exception) => {
					console.error(exception);
					if (exception.response.status === 401 || exception.response.status === 403) {
						logout();
						navigate(loginPage);
						setMsg({ msg: 'Nie masz uprawnień albo Twoja sesja wygasła' });
					} else {
						setMsg({ msg: 'Błąd serwera. Spróbuj za chwilę' });
					}
					closeDialog();
				});
		} else {
			closeDialog();
		}
	};

	const renderSettings = () => {
		return (
			<Grid
				item
				container
				flexWrap={'nowrap'}
				gap={'50px'}
				sx={{
					maxWidth: '100%',
				}}
			>
				<Grid
					item
					container
					flexDirection={'column'}
				>
					<Typography
						mt={5}
						variant={'h4'}
					>
						Ustawienia rejestracji
					</Typography>
					<Divider sx={{ mt: 2, mb: 2, borderColor: isHighContrast ? palette.primary.main : 'inherit' }} />
					<LocalizationProvider
						dateAdapter={AdapterDayjs}
						adapterLocale='pl'
					>
						<DateTimePicker
							label='Koniec rejestracji nauczycieli'
							value={olympics?.teachersRegistrationFinishDate}
							onChange={(newValue) => {
								if (olympics) {
									setOlympics({ ...olympics, teachersRegistrationFinishDate: newValue });
								}
							}}
							sx={{
								mt: 3,
								maxWidth: '50%',
								fontSize: '12px',
								color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
								'& *': {
									color: isHighContrast ? palette.primary.main : 'inherit',
								},
								'& label': {
									color: isHighContrast ? palette.primary.main : 'inherit',
								},
								'& .MuiOutlinedInput-root': {
									'&:hover fieldset': {
										borderColor: isHighContrast ? palette.primary.main : 'inherit',
									},
									'& fieldset': {
										borderColor: isHighContrast ? palette.primary.main : 'inherit',
									},
								},
							}}
						/>
						<FormControlLabel
							control={
								<Checkbox
									checked={olympics?.isStudentRegisterEnabled}
									onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
										if (olympics) {
											setOlympics({ ...olympics, isStudentRegisterEnabled: event.target.checked });
										}
									}}
									sx={{
										color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
										'&.Mui-checked': {
											color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
										},
									}}
								/>
							}
							label='Włącz rejestrację uczniów'
						/>
						<DateTimePicker
							label='Koniec rejestracji uczniów'
							value={olympics?.studentsRegistrationFinishDate}
							onChange={(newValue) => {
								if (olympics) {
									setOlympics({ ...olympics, studentsRegistrationFinishDate: newValue });
								}
							}}
							sx={{
								mt: 3,
								maxWidth: '50%',
								fontSize: '12px',
								color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
								'& *': {
									color: isHighContrast ? palette.primary.main : 'inherit',
								},
								'& label': {
									color: isHighContrast ? palette.primary.main : 'inherit',
								},
								'& .MuiOutlinedInput-root': {
									'&:hover fieldset': {
										borderColor: isHighContrast ? palette.primary.main : 'inherit',
									},
									'& fieldset': {
										borderColor: isHighContrast ? palette.primary.main : 'inherit',
									},
								},
							}}
						/>
						<DateTimePicker
							label='Koniec potwierdzania uczestników'
							value={olympics?.registrationConfirmationFinishDate}
							onChange={(newValue) => {
								if (olympics) {
									setOlympics({ ...olympics, registrationConfirmationFinishDate: newValue });
								}
							}}
							sx={{
								mt: 3,
								maxWidth: '50%',
								fontSize: '12px',
								color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
								'& *': {
									color: isHighContrast ? palette.primary.main : 'inherit',
								},
								'& label': {
									color: isHighContrast ? palette.primary.main : 'inherit',
								},
								'& .MuiOutlinedInput-root': {
									'&:hover fieldset': {
										borderColor: isHighContrast ? palette.primary.main : 'inherit',
									},
									'& fieldset': {
										borderColor: isHighContrast ? palette.primary.main : 'inherit',
									},
								},
							}}
						/>
					</LocalizationProvider>
					<Typography
						mt={5}
						variant={'h4'}
					>
						Ustawienia etapów
					</Typography>
					<Divider sx={{ mt: 2, borderColor: isHighContrast ? palette.primary.main : 'inherit' }} />
					<Typography mt={5}>Etap I</Typography>
					<Typography
						mt={2}
						variant='body2'
						sx={{ fontStyle: 'italic', fontWeight: 600 }}
					>
						Ręczna kwalifikacja uczniów
					</Typography>
					<Typography mt={5}>Etap IIA</Typography>
					<Typography
						mt={2}
						variant='body2'
						sx={{ fontStyle: 'italic', fontWeight: 600 }}
					>
						Kwalifikacja na podstawie limitu punktów
					</Typography>
					<Typography
						mt={2}
						variant='body2'
						sx={{ fontStyle: 'italic' }}
					>
						Wariant 1
					</Typography>
					<SettingsRow
						firstChild={
							<TextField
								type={'number'}
								label={'Część pisemna nr 1'}
								fullWidth
								value={olympics?.secondStagePoints.partA.v1.part1}
								onChange={(event) => handlePointsChange(event, 'secondStagePoints.partA.v1.part1')}
								sx={{
									color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
									'& *': {
										color: isHighContrast ? palette.primary.main : 'inherit',
									},
									'& .MuiOutlinedInput-root': {
										'&:hover fieldset': {
											borderColor: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& fieldset': {
											borderColor: isHighContrast ? palette.primary.main : 'inherit',
										},
									},
								}}
							/>
						}
						secondChild={
							<TextField
								type={'number'}
								label={'Część pisemna nr 2'}
								fullWidth
								value={olympics?.secondStagePoints.partA.v1.part2}
								onChange={(event) => handlePointsChange(event, 'secondStagePoints.partA.v1.part2')}
								sx={{
									color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
									'& *': {
										color: isHighContrast ? palette.primary.main : 'inherit',
									},
									'& .MuiOutlinedInput-root': {
										'&:hover fieldset': {
											borderColor: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& fieldset': {
											borderColor: isHighContrast ? palette.primary.main : 'inherit',
										},
									},
								}}
							/>
						}
					/>
					<Typography
						mt={2}
						variant='body2'
						sx={{ fontStyle: 'italic' }}
					>
						Wariant 2
					</Typography>
					<SettingsRow
						firstChild={
							<TextField
								type={'number'}
								label={'Część pisemna nr 1'}
								fullWidth
								value={olympics?.secondStagePoints.partA.v2.part1}
								onChange={(event) => handlePointsChange(event, 'secondStagePoints.partA.v2.part1')}
								sx={{
									color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
									'& *': {
										color: isHighContrast ? palette.primary.main : 'inherit',
									},
									'& .MuiOutlinedInput-root': {
										'&:hover fieldset': {
											borderColor: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& fieldset': {
											borderColor: isHighContrast ? palette.primary.main : 'inherit',
										},
									},
								}}
							/>
						}
						secondChild={
							<TextField
								type={'number'}
								label={'Część pisemna nr 2'}
								fullWidth
								value={olympics?.secondStagePoints.partA.v2.part2}
								onChange={(event) => handlePointsChange(event, 'secondStagePoints.partA.v2.part2')}
								sx={{
									color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
									'& *': {
										color: isHighContrast ? palette.primary.main : 'inherit',
									},
									'& .MuiOutlinedInput-root': {
										'&:hover fieldset': {
											borderColor: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& fieldset': {
											borderColor: isHighContrast ? palette.primary.main : 'inherit',
										},
									},
								}}
							/>
						}
					/>
					<Typography
						mt={2}
						variant='body2'
						sx={{ fontStyle: 'italic' }}
					>
						Łączny limit z części pisemnej
					</Typography>
					<SettingsRow
						firstChild={
							<TextField
								type={'number'}
								label={'Łączny limit z części 1 i 2'}
								fullWidth
								value={olympics?.secondStagePoints.partA.total}
								onChange={(event) => handlePointsChange(event, 'secondStagePoints.partA.total')}
								sx={{
									color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
									'& *': {
										color: isHighContrast ? palette.primary.main : 'inherit',
									},
									'& .MuiOutlinedInput-root': {
										'&:hover fieldset': {
											borderColor: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& fieldset': {
											borderColor: isHighContrast ? palette.primary.main : 'inherit',
										},
									},
								}}
							/>
						}
					/>
					<Typography mt={5}>Etap IIB</Typography>
					<Typography
						mt={2}
						variant='body2'
						sx={{ fontStyle: 'italic', fontWeight: 600 }}
					>
						Kwalifikacja na podstawie decyzji administratora
					</Typography>
					<SettingsRow
						firstChild={
							<TextField
								type={'number'}
								label={'Część ustna nr 1'}
								fullWidth
								value={olympics?.secondStagePoints.partB.part1}
								onChange={(event) => handlePointsChange(event, 'secondStagePoints.partB.part1')}
								sx={{
									color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
									'& *': {
										color: isHighContrast ? palette.primary.main : 'inherit',
									},
									'& .MuiOutlinedInput-root': {
										'&:hover fieldset': {
											borderColor: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& fieldset': {
											borderColor: isHighContrast ? palette.primary.main : 'inherit',
										},
									},
								}}
							/>
						}
						secondChild={
							<TextField
								type={'number'}
								label={'Część ustna nr 2'}
								fullWidth
								value={olympics?.secondStagePoints.partB.part2}
								onChange={(event) => handlePointsChange(event, 'secondStagePoints.partB.part2')}
								sx={{
									color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
									'& *': {
										color: isHighContrast ? palette.primary.main : 'inherit',
									},
									'& .MuiOutlinedInput-root': {
										'&:hover fieldset': {
											borderColor: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& fieldset': {
											borderColor: isHighContrast ? palette.primary.main : 'inherit',
										},
									},
								}}
							/>
						}
						thirdChild={
							<TextField
								label={'Część ustna nr 3'}
								fullWidth
								value={olympics?.secondStagePoints.partB.part3}
								onChange={(event) => handlePointsChange(event, 'secondStagePoints.partB.part3')}
								sx={{
									color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
									'& *': {
										color: isHighContrast ? palette.primary.main : 'inherit',
									},
									'& .MuiOutlinedInput-root': {
										'&:hover fieldset': {
											borderColor: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& fieldset': {
											borderColor: isHighContrast ? palette.primary.main : 'inherit',
										},
									},
								}}
							/>
						}
					/>
					<Typography
						mt={2}
						variant='body2'
						sx={{ fontStyle: 'italic' }}
					>
						Łączny limit punktów z części pisemnej i ustnej
					</Typography>
					<SettingsRow
						firstChild={
							<TextField
								type={'number'}
								label={'Łączny limit z części IIA i IIB'}
								fullWidth
								value={olympics?.secondStagePoints.partB.total}
								onChange={(event) => handlePointsChange(event, 'secondStagePoints.partB.total')}
								sx={{
									color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
									'& *': {
										color: isHighContrast ? palette.primary.main : 'inherit',
									},
									'& .MuiOutlinedInput-root': {
										'&:hover fieldset': {
											borderColor: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& fieldset': {
											borderColor: isHighContrast ? palette.primary.main : 'inherit',
										},
									},
								}}
							/>
						}
					/>
					<Typography
						mt={2}
						variant='body2'
						sx={{ fontStyle: 'italic' }}
					>
						Limit liczby uczniów
					</Typography>
					<SettingsRow
						firstChild={
							<TextField
								type={'number'}
								label={'Limit liczby uczniów'}
								fullWidth
								value={olympics?.secondStagePoints.partB.totalStudentsLimit}
								onChange={(event) => handlePointsChange(event, 'secondStagePoints.partB.totalStudentsLimit')}
								sx={{
									color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
									'& *': {
										color: isHighContrast ? palette.primary.main : 'inherit',
									},
									'& .MuiOutlinedInput-root': {
										'&:hover fieldset': {
											borderColor: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& fieldset': {
											borderColor: isHighContrast ? palette.primary.main : 'inherit',
										},
									},
								}}
							/>
						}
					/>
					<SettingsRow
						firstChild={
							<LocalizationProvider
								dateAdapter={AdapterDayjs}
								adapterLocale='pl'
							>
								<DateTimePicker
									label='Koniec wprowadzania wyników przez sekretarzy'
									value={olympics?.secondStagePoints?.partB?.stageClosedDate}
									onChange={(newValue) => {
										if (olympics) {
											handleValueChange('secondStagePoints.partB.stageClosedDate', newValue);
										}
									}}
									sx={{
										mt: 3,
										width: '100%',
										fontSize: '12px',
										color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
										'& *': {
											color: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& .MuiOutlinedInput-root': {
											'&:hover fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
											'& fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
										},
									}}
								/>
							</LocalizationProvider>
						}
					/>
					<Typography mt={5}>Etap IIIA</Typography>
					<SettingsRow
						firstChild={
							<TextField
								type={'number'}
								label={'Część pisemna'}
								fullWidth
								value={olympics?.thirdStagePoints.partA.points}
								onChange={(event) => handlePointsChange(event, 'thirdStagePoints.partA.points')}
								sx={{
									color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
									'& *': {
										color: isHighContrast ? palette.primary.main : 'inherit',
									},
									'& .MuiOutlinedInput-root': {
										'&:hover fieldset': {
											borderColor: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& fieldset': {
											borderColor: isHighContrast ? palette.primary.main : 'inherit',
										},
									},
								}}
							/>
						}
					/>
					<Typography
						mt={2}
						variant='body2'
						sx={{ fontStyle: 'italic', fontWeight: 600 }}
					>
						Finalista
					</Typography>
					<SettingsRow
						firstChild={
							<TextField
								type={'number'}
								label={'Limit punktów dla Finalisty'}
								fullWidth
								value={olympics?.thirdStagePoints.partA.finalistPoints}
								onChange={(event) => handlePointsChange(event, 'thirdStagePoints.partA.finalistPoints')}
								sx={{
									color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
									'& *': {
										color: isHighContrast ? palette.primary.main : 'inherit',
									},
									'& .MuiOutlinedInput-root': {
										'&:hover fieldset': {
											borderColor: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& fieldset': {
											borderColor: isHighContrast ? palette.primary.main : 'inherit',
										},
									},
								}}
							/>
						}
					/>
					<Typography mt={5}>Etap IIIB</Typography>
					<Typography
						mt={2}
						variant='body2'
						sx={{ fontStyle: 'italic', fontWeight: 600 }}
					>
						Laureaci
					</Typography>
					<SettingsRow
						firstChild={
							<TextField
								type={'number'}
								label={'Laureat III stopnia'}
								fullWidth
								value={olympics?.thirdStagePoints.partB.laureate3}
								onChange={(event) => handlePointsChange(event, 'thirdStagePoints.partB.laureate3')}
								sx={{
									color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
									'& *': {
										color: isHighContrast ? palette.primary.main : 'inherit',
									},
									'& .MuiOutlinedInput-root': {
										'&:hover fieldset': {
											borderColor: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& fieldset': {
											borderColor: isHighContrast ? palette.primary.main : 'inherit',
										},
									},
								}}
							/>
						}
						secondChild={
							<TextField
								type={'number'}
								label={'Laureat II stopnia'}
								fullWidth
								value={olympics?.thirdStagePoints.partB.laureate2}
								onChange={(event) => handlePointsChange(event, 'thirdStagePoints.partB.laureate2')}
								sx={{
									color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
									'& *': {
										color: isHighContrast ? palette.primary.main : 'inherit',
									},
									'& .MuiOutlinedInput-root': {
										'&:hover fieldset': {
											borderColor: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& fieldset': {
											borderColor: isHighContrast ? palette.primary.main : 'inherit',
										},
									},
								}}
							/>
						}
						thirdChild={
							<TextField
								type={'number'}
								label={'Laureat I stopnia'}
								fullWidth
								value={olympics?.thirdStagePoints.partB.laureate1}
								onChange={(event) => handlePointsChange(event, 'thirdStagePoints.partB.laureate1')}
								sx={{
									color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
									'& *': {
										color: isHighContrast ? palette.primary.main : 'inherit',
									},
									'& .MuiOutlinedInput-root': {
										'&:hover fieldset': {
											borderColor: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& fieldset': {
											borderColor: isHighContrast ? palette.primary.main : 'inherit',
										},
									},
								}}
							/>
						}
					/>
				</Grid>
				<Grid
					item
					container
					flexDirection={'column'}
				>
					<Typography
						mt={5}
						variant={'h4'}
					>
						Opcje
					</Typography>
					<Divider sx={{ mt: 2, mb: 2, borderColor: isHighContrast ? palette.primary.main : 'inherit' }} />
					<Button
						startIcon={<SaveIcon />}
						variant={'contained'}
						onClick={() => {
							setDialogContent('Czy napewno chcesz zmienić ustawienia olimpiady?');
							setOkFunction(() => updateOlympics);
						}}
					>
						Zapisz zmiany
					</Button>
					<Button
						startIcon={<ReplayIcon />}
						variant={'contained'}
						color={'error'}
						onClick={() => setOpenNewOlympicsDialog(true)}
						sx={{
							mt: '100px',
							mb: 2,
							backgroundColor: isHighContrast ? palette.primary.main + '!important' : '',
							color: isHighContrast ? palette.primary.light + '!important' : '',
						}}
					>
						Nowa olimpiada
					</Button>
					<Typography>- tworzy nową edycję olimpiady</Typography>
					<Typography>- usuwa dane uczniów i nauczycieli</Typography>
					<Grid
						item
						container
						flexDirection={'column'}
					>
						<Typography
							mt={5}
							variant={'h4'}
						>
							Dyplomy
						</Typography>
						<Divider sx={{ mt: 2, mb: 2, borderColor: isHighContrast ? palette.primary.main : 'inherit' }} />
						<Button
							sx={{ mb: 4 }}
							startIcon={<AssignmentIcon />}
							variant={'contained'}
							onClick={() => {
								setDialogContent('Czy napewno chcesz wygenerować dyplomy z części pisemnej?');
								setOkFunction(() => () => generateCertificates('written'));
							}}
						>
							Generuj certyfikaty z części pisemnej
						</Button>
						<Button
							startIcon={<AssignmentIcon />}
							variant={'contained'}
							onClick={() => {
								setDialogContent('Czy napewno chcesz wygenerować dyplomy z części ustnej?');
								setOkFunction(() => () => generateCertificates('oral'));
							}}
						>
							Generuj certyfikaty z części ustnej
						</Button>
					</Grid>
					<Grid
						item
						container
						flexDirection={'column'}
					>
						<Typography
							mt={5}
							variant={'h4'}
						>
							Ustawienia generalne
						</Typography>
						<Divider sx={{ mt: 2, mb: 2, borderColor: isHighContrast ? palette.primary.main : 'inherit' }} />
						<SettingsRow
							firstChild={
								<TextField
									type={'text'}
									label={'Edycja Olimpiady'}
									fullWidth
									value={olympics?.edition}
									onChange={(newValue) => {
										if (olympics) {
											handleSettings('edition', newValue);
										}
									}}
									sx={{
										color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
										'& *': {
											color: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& .MuiOutlinedInput-root': {
											'&:hover fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
											'& fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
										},
									}}
								/>
							}
						/>
						<SettingsRow
							firstChild={
								<TextField
									type={'text'}
									label={'Rok szkolny'}
									fullWidth
									value={olympics?.schoolYear}
									onChange={(newValue) => {
										if (olympics) {
											handleSettings('schoolYear', newValue);
										}
									}}
									sx={{
										color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
										'& *': {
											color: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& .MuiOutlinedInput-root': {
											'&:hover fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
											'& fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
										},
									}}
								/>
							}
						/>
					</Grid>
				</Grid>
			</Grid>
		);
	};

	return (
		<>
			{!olympics ? <Loader /> : renderSettings()}
			<NewOlympicsDialog
				open={openNewOlympicsDialog}
				close={() => setOpenNewOlympicsDialog(false)}
				onSuccess={() => {}}
			/>
			<ConfirmationDialog
				close={() => closeDialog()}
				inProgress={sendingRequest}
				text={dialogContent}
				onSuccess={() => okFunction && okFunction()}
			/>
		</>
	);
};

export default SettingsPage;
